import React from "react";
import styled from "styled-components";
import { layout, LayoutProps } from "styled-system";
import {
  Box,
  Button,
  Card,
  CircularLoader,
  ResponsiveLayout,
  Typography,
} from "@bca/ui-library";
import css from "@styled-system/css";

import { Prices } from "../vehicleDetails/vehicle-details-props.type";
import { formatDate, formatWeekday } from "../helpers/date.functions";
import useSourcePage from "../hooks/useSourcePage";

interface LiProps extends LayoutProps {
  children: React.ReactNode;
}

const GreyBackground = styled.div<{ id: string; children: React.ReactNode }>`
  background-color: #fafafa;
  padding-bottom: 25px;
`;

const Row = styled(Box)`
  @media screen and (max-width: 820px) {
    flex-direction: column;
    height: auto;
    padding-bottom: 20px;
    & {
      ul {
        flex-direction: column;
        padding: 0;
      }
      li {
        flex-direction: row;
        width: 100%;
        max-width: 350px;
        justify-content: space-between;
        margin: 12px 0;
      }
    }

    & > h6 {
      margin: 8px 0;
    }
  }
  @media screen and (max-width: 620px) {
    & {
      li {
        max-width: 220px;
      }
    }
  }
`;

const ValuationRow = styled(Box)`
  border-bottom: 1px dashed #f0f0f0;
  box-sizing: content-box;

  @media screen and (max-width: 820px) {
    max-width: 350px;
    justify-content: space-between;
  }
  @media screen and (max-width: 620px) {
    max-width: 220px;
    justify-content: space-between;
  }
`;

const Ul = styled.ul`
  list-style: none;
  display: flex;
  flex: 1;
  margin: 0;
`;

const Li = styled.li<LiProps>`
  display: flex;
  flex-direction: column;
  align-items: left;
  ${layout}
`;

const Price = styled.span`
  margin-top: auto;
  font-size: 20px;
  font-weight: 500;
  color: #070b16;
  line-height: 1.4;
`;

const PricingGuideCard = styled(Card)`
  ${css({
    p: [20, 20, 36],
  })}
`;

interface PricingGuideProps {
  prices: Prices;
  soldDate: string | null;
  bcaValuation: string | null;
  setShowValuationModal: React.Dispatch<React.SetStateAction<boolean>>;
  isBCAValuationLoading: boolean;
  isBCAValuationAvailable: boolean;
}

const PricingGuide: React.FunctionComponent<PricingGuideProps> = ({
  prices,
  soldDate,
  bcaValuation,
  setShowValuationModal,
  isBCAValuationLoading,
  isBCAValuationAvailable,
}) => {
  const { isVPSoldSourcePage, isVPExceptSoldPageOrTransportTab } =
    useSourcePage();

  const buildPriceLabel = (price: any): string => {
    if (typeof price === "string" && price !== "") {
      const priceWithoutWhiteSpace = price?.toString()?.replace(/\s/g, "");
      return priceWithoutWhiteSpace;
    }
    return "-";
  };

  const formatBCAValuationPrice = (price: string | null): string => {
    if (!isBCAValuationAvailable) return "Data Unavailable";
    const priceWithoutWhiteSpace = price?.toString()?.replace(/\s/g, "");
    const formattedPrice = priceWithoutWhiteSpace?.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ","
    );
    return `£${formattedPrice}`;
  };

  return (
    <GreyBackground id="guide-pricing">
      <ResponsiveLayout pt={9}>
        <PricingGuideCard marginX={0} marginY={0} width="100%" height="100%">
          <Typography variant="h5" textAlign="left" my={0} css={css}>
            Guide Pricing
          </Typography>
          {isVPSoldSourcePage && Boolean(soldDate) && (
            <Box maxWidth={440} marginTop={18} marginBottom={2}>
              <Typography variant="body-small" m={0}>
                Shown below is the guide pricing for this vehicle on the date of
                its sale. This vehicle was sold on{" "}
                <Typography
                  variant="body-small"
                  fontWeight="semiBold"
                  htmlTag="span"
                >
                  {formatWeekday(soldDate)} {formatDate(soldDate)}
                </Typography>
                .
              </Typography>
            </Box>
          )}
          <Row
            height="60px"
            display="flex"
            pt={3}
            pb={5}
            css={`
              border-bottom: 1px dashed #f0f0f0;
              box-sizing: content-box;
            `}
          >
            <Typography
              variant="h6"
              marginTop="auto"
              marginBottom={0}
              css={`
                min-width: ${isVPExceptSoldPageOrTransportTab
                  ? "117px"
                  : "100px"};
                font-size: 18px;
              `}
            >
              CAP HPI*
            </Typography>
            <Ul>
              <Li width={1 / 5}>
                <Typography htmlTag="span" variant="body-small" margin={0}>
                  New
                </Typography>
                <Price>{buildPriceLabel(prices?.CAPNew.value)}</Price>
              </Li>
              <Li width={1 / 5}>
                <Typography variant="body-small" margin={0}>
                  Retail
                </Typography>
                <Price>{buildPriceLabel(prices?.CAPRetail.value)}</Price>
              </Li>
              <Li width={1 / 5}>
                <Typography variant="body-small" margin={0}>
                  Clean
                </Typography>
                <Price>{buildPriceLabel(prices?.CAPClean.value)}</Price>
              </Li>
              <Li width={1 / 5}>
                <Typography variant="body-small" margin={0}>
                  Average
                </Typography>
                <Price>{buildPriceLabel(prices?.CAPAverage.value)}</Price>
              </Li>
              <Li width={1 / 5}>
                <Typography variant="body-small" margin={0}>
                  Below Average
                </Typography>
                <Price>{buildPriceLabel(prices?.CAPBelowAverage.value)}</Price>
              </Li>
            </Ul>
          </Row>
          {isVPExceptSoldPageOrTransportTab && (
            <ValuationRow display="flex" flexDirection="row" py={5}>
              <Box>
                <Typography
                  variant="h6"
                  marginTop="auto"
                  marginBottom={0}
                  css={`
                    min-width: 100px;
                    font-size: 18px;
                  `}
                >
                  BCA Valuation
                </Typography>
                <Button
                  variant="tertiary"
                  paddingX={0}
                  height="28px"
                  onClick={() => setShowValuationModal(true)}
                  css={`
                    font-size: 14px;
                  `}
                >
                  Learn More
                </Button>
              </Box>
              <Box marginLeft={42}>
                {isBCAValuationLoading ? (
                  <Box display="flex" flexDirection="row">
                    <Typography
                      htmlTag="span"
                      variant="body-large"
                      fontWeight="semiBold"
                      color="footer-blue"
                      marginRight={1}
                    >
                      Pending
                    </Typography>
                    <Box marginTop={1}>
                      <CircularLoader
                        title="Load BCA Valuation"
                        width={20}
                        height={20}
                      />
                    </Box>
                  </Box>
                ) : (
                  <>
                    {isBCAValuationAvailable ? (
                      <Price>{formatBCAValuationPrice(bcaValuation)}</Price>
                    ) : (
                      <Typography
                        htmlTag="span"
                        variant="body"
                        fontWeight="semiBold"
                        color="footer-blue"
                      >
                        Data Unavailable
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </ValuationRow>
          )}
          {!isVPExceptSoldPageOrTransportTab && (
            <Row
              height="60px"
              display="flex"
              p={5}
              pl={0}
              css={`
                border-bottom: 1px dashed #f0f0f0;
                box-sizing: content-box;
              `}
            >
              <Typography
                variant="h6"
                marginTop="auto"
                marginBottom={0}
                css={`
                  min-width: 100px;
                  font-size: 18px;
                `}
              >
                Glass's
              </Typography>
              <Ul>
                <Li width={1 / 5}>
                  <Typography variant="body-small" margin={0}>
                    New
                  </Typography>
                  <Price>{buildPriceLabel(prices.GlassNew.value)}</Price>
                </Li>
                <Li width={1 / 5}>
                  <Typography variant="body-small" margin={0}>
                    Retail
                  </Typography>
                  <Price>{buildPriceLabel(prices.GlassRetail.value)}</Price>
                </Li>
                <Li width={1 / 5}>
                  <Typography variant="body-small" margin={0}>
                    Trade
                  </Typography>
                  <Price>{buildPriceLabel(prices.GlassTrade.value)}</Price>
                </Li>
              </Ul>
            </Row>
          )}
          <Typography
            variant="h6"
            mt={25}
            mb={0}
            color="grey-blue"
            css={`
              font-size: 14px;
              font-weight: normal;
              @media screen and (max-width: 820px) {
                font-size: 14px;
              }
            `}
          >
            * CAP HPI data and software is protected by copyright and database
            rights. All rights reserved.
          </Typography>
        </PricingGuideCard>
      </ResponsiveLayout>
    </GreyBackground>
  );
};

export default PricingGuide;
